import { useState } from "react";
import type { PermissionRole } from "@/shared/lib/roles";
import { type RowDraft, rosterRowSchema } from "./roster.schemas";
import type { RosterUser, UserUpdate } from "./roster.types";

// Server strings → editable draft (displayName "" = no name).
function toDraft(u: RosterUser): RowDraft {
  return {
    displayName: u.displayName ?? "",
    permissionRole: u.permissionRole as PermissionRole,
  };
}

function draftEquals(a: RowDraft, b: RowDraft): boolean {
  return (
    a.displayName === b.displayName && a.permissionRole === b.permissionRole
  );
}

// Edits are cleared ONLY via clear() (on successful save) — never on a `users` refetch, so an
// unrelated invalidation (e.g. deleting another row) doesn't discard in-flight edits.
export function useRosterDrafts(users: RosterUser[]) {
  const [edits, setEdits] = useState<Record<string, RowDraft>>({});

  const draftFor = (u: RosterUser): RowDraft => edits[u.id] ?? toDraft(u);
  const onDraftChange = (userId: string, next: RowDraft) =>
    setEdits((prev) => ({ ...prev, [userId]: next }));

  const dirtyRows: { user: RosterUser; draft: RowDraft }[] = [];
  for (const u of users) {
    const draft = edits[u.id];
    if (draft && !draftEquals(draft, toDraft(u))) {
      dirtyRows.push({ user: u, draft });
    }
  }

  // Validates every dirty draft; on failure returns the first issue's (Swedish) schema message.
  const buildUpdates = ():
    | { ok: true; updates: UserUpdate[] }
    | { ok: false; message: string } => {
    const updates: UserUpdate[] = [];
    for (const { user, draft } of dirtyRows) {
      const parsed = rosterRowSchema.safeParse({ userId: user.id, ...draft });
      if (!parsed.success) {
        return {
          ok: false,
          message: parsed.error.issues[0]?.message ?? "Ogiltiga ändringar.",
        };
      }
      updates.push(parsed.data);
    }
    return { ok: true, updates };
  };

  const clear = () => setEdits({});

  return { draftFor, onDraftChange, dirtyRows, buildUpdates, clear };
}
